import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Server, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
  getLocalServerUrl,
  getLocalServerOverrides,
  setLocalServerOverrides,
} from "@/lib/dataGateway";

const BUILD_DEFAULT_URL =
  (import.meta.env.VITE_LOCAL_SERVER_URL as string | undefined) || "http://localhost:4477";

/**
 * Configuração do Servidor Local POR DISPOSITIVO (localStorage). Necessária porque o build do
 * Lovable é único para todas as lojas — as VITE_* seriam globais. Cada PC de caixa aponta para o
 * seu servidor local + chave, sem rebuild. Cadeia de leitura no DataGateway:
 * localStorage → VITE_* → default localhost:4477.
 */
export function LocalServerConfig() {
  const overrides = getLocalServerOverrides();
  const [url, setUrl] = useState(overrides.url ?? "");
  const [apiKey, setApiKey] = useState(overrides.apiKey ?? "");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const usingOverride = !!(overrides.url || overrides.apiKey);

  const handleSave = () => {
    setLocalServerOverrides(url, apiKey);
    setResult(null);
    toast.success("Configuração do servidor local salva neste dispositivo.");
  };

  const handleClear = () => {
    setLocalServerOverrides(null, null);
    setUrl("");
    setApiKey("");
    setResult(null);
    toast.success("Voltou ao padrão do build.");
  };

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    // Testa os valores DO FORMULÁRIO (permite validar antes de salvar).
    const base = (url.trim() || getLocalServerUrl()).replace(/\/+$/, "");
    const key = apiKey.trim();
    try {
      const health = await fetch(`${base}/health`);
      if (!health.ok) {
        setResult({ ok: false, msg: `Servidor respondeu ${health.status} no /health (Postgres local fora?).` });
        return;
      }
      // Endpoint protegido: valida a chave (401 = chave errada/ausente quando há auth).
      const status = await fetch(`${base}/api/sync/status`, {
        headers: key ? { "X-API-Key": key } : {},
      });
      if (status.status === 401) {
        setResult({ ok: false, msg: "Conectou, mas a Chave de API está incorreta/ausente (401)." });
        return;
      }
      if (!status.ok) {
        setResult({ ok: false, msg: `Endpoint protegido respondeu ${status.status}.` });
        return;
      }
      setResult({ ok: true, msg: "Conexão OK — servidor alcançável e chave válida." });
    } catch {
      setResult({
        ok: false,
        msg: "Não foi possível conectar (verifique a URL, a rede, ou bloqueio do navegador).",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Server className="h-5 w-5" />
          Servidor Local (AltusFood Local Server)
        </CardTitle>
        <CardDescription>
          Endereço e chave do servidor local desta loja. Configuração POR DISPOSITIVO (salva só neste
          navegador). Em branco = usa o padrão do sistema ({BUILD_DEFAULT_URL}).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="ls-url">URL do Servidor Local</Label>
          <Input
            id="ls-url"
            placeholder={BUILD_DEFAULT_URL}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ls-key">Chave de API (X-API-Key)</Label>
          <Input
            id="ls-key"
            type="password"
            placeholder="(deixe em branco se o servidor não exige chave)"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
          />
        </div>

        {result && (
          <div className={`flex items-start gap-2 text-sm ${result.ok ? "text-green-600" : "text-destructive"}`}>
            {result.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5" /> : <XCircle className="h-4 w-4 mt-0.5" />}
            <span>{result.msg}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleTest} disabled={testing}>
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Testar conexão
          </Button>
          <Button onClick={handleSave} disabled={testing}>Salvar</Button>
          {usingOverride && (
            <Button variant="ghost" onClick={handleClear} disabled={testing}>
              Usar padrão do sistema
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {usingOverride
            ? "Usando a configuração deste dispositivo."
            : "Usando o padrão do sistema (nenhuma configuração local salva)."}
        </p>
      </CardContent>
    </Card>
  );
}
